@file:Suppress("UnsafeCastFromDynamic")

package com.zi.support.web

import com.zi.support.shared.CalendarSync
import kotlinx.browser.document
import kotlinx.browser.window
import kotlin.js.Date
import kotlin.js.json

/**
 * The signed-in person's primary Google Calendar, through Google Identity Services in the browser.
 * Phones use their own calendar instead (DeviceCalendar on Android, EventKit on iOS).
 */
internal object GoogleCalendar {
    /** Web OAuth client ID from Google Cloud > APIs & Services > Credentials. Blank hides the feature. */
    private const val CLIENT_ID = ""
    private const val SCOPE = "https://www.googleapis.com/auth/calendar.events"
    private const val API = "https://www.googleapis.com/calendar/v3/calendars/primary/events"
    private const val TOKEN_KEY = "google-calendar-token"
    private const val DAY = 24.0 * 60 * 60 * 1000

    data class Meeting(val id: String, val title: String, val description: String, val location: String, val startAt: Double)
    data class Entry(val title: String, val begin: Double, val allDay: Boolean, val location: String, val link: String)

    val configured get() = CLIENT_ID.isNotBlank()
    private var running = false
    private var again: (() -> Unit)? = null

    /** Loads the Google sign-in script ahead of the click, so its popup opens inside the click and is not blocked. */
    fun preload() {
        if (!configured || document.getElementById("gsi-client") != null) return
        val script = document.createElement("script").asDynamic()
        script.id = "gsi-client"; script.src = "https://accounts.google.com/gsi/client"; script.async = true
        document.head?.appendChild(script)
    }

    fun connected() = token() != null

    fun connect(loginHint: String, done: () -> Unit) {
        val oauth = window.asDynamic().google?.accounts?.oauth2
        if (oauth == null) { toast("Google sign-in is still loading. Try again in a moment.", true); return }
        val config = json("client_id" to CLIENT_ID, "scope" to SCOPE, "login_hint" to loginHint, "callback" to { response: dynamic ->
            if (response.access_token != null) {
                store(json("token" to response.access_token, "expiresAt" to Date.now() + (response.expires_in as Number).toDouble() * 1000 - 60_000).let { JSON.stringify(it) })
                done()
            } else toast("Google Calendar was not connected.", true)
        })
        oauth.initTokenClient(config).requestAccessToken()
    }

    /**
     * Adds [meetings] missing from the calendar, then hands back the person's other entries for the next
     * [CalendarSync.SHOW_DAYS] days. A call made while one is running runs again once it finishes.
     */
    fun sync(uid: String, meetings: List<Meeting>, done: (List<Entry>) -> Unit) {
        val token = token() ?: return
        if (running) { again = { sync(uid, meetings, done) }; return }
        running = true
        val now = Date.now(); val todayStart = js("new Date(new Date().setHours(0,0,0,0)).getTime()") as Double
        val query = "?singleEvents=true&orderBy=startTime&maxResults=2500" +  // ponytail: one page of 2,500 events; page with nextPageToken if anyone gets close
            "&timeMin=${param(iso(todayStart))}&timeMax=${param(iso(now + CalendarSync.SYNC_DAYS * DAY))}" +
            "&fields=items(summary,description,location,htmlLink,start,end)"
        fun finish(entries: List<Entry>) { running = false; done(entries); again?.let { again = null; it() } }
        call(token, "GET", query, null).then({ body: dynamic ->
            val items = body.items; val copied = mutableSetOf<String>(); val others = mutableListOf<Entry>()
            for (i in 0 until (items?.length as? Int ?: 0)) {
                val item = items[i]
                val meetingId = CalendarSync.meetingIdIn(item.description as? String)
                val allDay = item.start?.dateTime == null
                // A date-only start is an all-day event; without a zone it parses as local midnight.
                val begin = if (allDay) Date.parse((item.start.date as String) + "T00:00:00") else Date.parse(item.start.dateTime as String)
                if (meetingId != null) copied += meetingId
                else if (begin < todayStart + CalendarSync.SHOW_DAYS * DAY) others += Entry((item.summary as? String).orEmpty().ifBlank { "(No title)" }, begin, allDay, (item.location as? String).orEmpty(), (item.htmlLink as? String).orEmpty())
            }
            // Remembers what was added here too, so a copy the person deleted from their calendar is not re-added.
            val doneKey = "google-calendar-copied-$uid"
            val added = (runCatching { JSON.parse<Array<String>>(window.localStorage.getItem(doneKey) ?: "[]") }.getOrNull() ?: emptyArray()).toMutableSet()
            val inserts = meetings.filter { CalendarSync.needsCopy(it.startAt.toLong(), now.toLong(), it.id in copied || it.id in added) }.map { meeting ->
                call(token, "POST", "", json("summary" to meeting.title, "description" to meeting.description, "location" to meeting.location,
                    "start" to json("dateTime" to iso(meeting.startAt)), "end" to json("dateTime" to iso(meeting.startAt + CalendarSync.DURATION_MILLIS))))
                    .then({ _: dynamic -> added += meeting.id })
            }.toTypedArray()
            js("Promise").allSettled(inserts).then({ _: dynamic ->
                runCatching { window.localStorage.setItem(doneKey, JSON.stringify(added.toTypedArray())) }
                finish(others)
            })
        }, { error: dynamic -> console.warn("Google Calendar sync failed", error); finish(emptyList()) })
    }

    private fun call(token: String, method: String, path: String, body: dynamic): dynamic {
        val init: dynamic = json("method" to method, "headers" to json("Authorization" to "Bearer $token", "Content-Type" to "application/json"))
        if (body != null) init.body = JSON.stringify(body)
        return window.asDynamic().fetch(API + path, init).then({ response: dynamic ->
            if (response.status == 401) store(null)  // expired or revoked: show Connect again
            if (response.ok != true) throw IllegalStateException("Google Calendar returned ${response.status}")
            response.json()
        })
    }

    private fun iso(millis: Double) = Date(millis).toISOString()
    private fun param(value: String) = js("encodeURIComponent")(value) as String

    private fun token(): String? = runCatching {
        val saved = JSON.parse<dynamic>(window.sessionStorage.getItem(TOKEN_KEY) ?: return null)
        if ((saved.expiresAt as Double) > Date.now()) saved.token as String else null
    }.getOrNull()

    private fun store(value: String?) {
        runCatching { if (value == null) window.sessionStorage.removeItem(TOKEN_KEY) else window.sessionStorage.setItem(TOKEN_KEY, value) }
    }
}
